import { Controller } from "@hotwired/stimulus";

// Mobile sub-tabs of the affiliate dashboard list area (Figma 7:291): toggles
// between the two list cards. On desktop both cards are visible and the tab
// row is hidden — purely presentational, no server round-trip.
//
// Follows the ARIA tabs pattern: aria-selected plus a roving tabindex, so the
// pair is operable with the arrow keys / Home / End.
//
// When `tabKeysValue` is set (program detail page), a tab switch also patches
// every range-selector link inside the enclosing turbo frame so the server can
// restore the active tab after a range change.
export default class extends Controller {
  static targets = ["tab", "panel"];
  static values = {
    defaultTab: { type: Number, default: 0 },
    tabKeys: { type: Array, default: [] },
  };

  // Restore the tab index the server told us about (via defaultTabValue), so a
  // Turbo frame replacement that carries a `tab` param does not jump the user
  // back to the first tab. Falls back to 0 for every caller that does not set
  // the value — which keeps the Turbo-cache reset the old code relied on.
  connect() {
    this.activate(this.defaultTabValue);
  }

  show(event) {
    this.activate(this.tabTargets.indexOf(event.currentTarget));
  }

  navigate(event) {
    const current = this.tabTargets.indexOf(event.currentTarget);
    const last = this.tabTargets.length - 1;
    const next = {
      ArrowRight: current === last ? 0 : current + 1,
      ArrowLeft: current === 0 ? last : current - 1,
      Home: 0,
      End: last,
    }[event.key];

    if (next === undefined) return;

    event.preventDefault();
    this.activate(next);
    this.tabTargets[next].focus();
  }

  activate(index) {
    if (index < 0) return;

    this.tabTargets.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panelTargets.forEach((panel, i) =>
      panel.classList.toggle("is-active", i === index)
    );

    this.syncTabParam(index);
  }

  // When tab keys are configured, patch the `tab` query param into every
  // range-selector link inside the same turbo frame. This way the server knows
  // which tab to restore after a range pill click — no full-page reload needed.
  syncTabParam(index) {
    const keys = this.tabKeysValue;
    if (!keys.length) return;

    const key = keys[index] || keys[0];
    const isDefault = index === 0;

    // Patch all links with data-turbo-action inside the enclosing turbo frame.
    const frame = this.element.closest("turbo-frame") || this.element.closest("[data-turbo-frame]")?.closest("turbo-frame") || document;
    frame.querySelectorAll("a[data-turbo-action]").forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (isDefault) {
        url.searchParams.delete("tab");
      } else {
        url.searchParams.set("tab", key);
      }
      link.href = url.toString();
    });
  }
}
